import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateOccupancyTypeRequestSchema,
  type CreateOccupancyTypeRequest,
  type OccupancyTypeDetail,
  type OccupancyTypeListItem,
  UpdateOccupancyTypeRequestSchema,
  type UpdateOccupancyTypeRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateOccupancyType, useUpdateOccupancyType } from '../use-occupancy-types';

import { MasterCodeField } from '@/components/master-code-field';

interface OccupancyTypeFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  occupancyType?: OccupancyTypeDetail | OccupancyTypeListItem | null;
}

const CREATE_DEFAULTS: CreateOccupancyTypeRequest = {
  code: '',
  name: '',
  paxCount: 1,
  sortOrder: 0,
  isActive: true,
};

const OccupancyTypeFields = ({
  pending,
  isCreate,
}: {
  pending: boolean;
  isCreate: boolean;
}): React.JSX.Element => (
  <>
    <MasterCodeField isCreate={isCreate} required />
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Double"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="paxCount">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Pax count"
          precision={0}
          min={1}
          max={20}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 1)}
          error={error}
          disabled={pending}
          required
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive occupancy types are hidden from itinerary dropdowns."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const OccupancyTypeFormDrawer = ({
  open,
  onOpenChange,
  mode,
  occupancyType,
}: OccupancyTypeFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createOccupancyType = useCreateOccupancyType();
  const updateOccupancyType = useUpdateOccupancyType();
  const pending = createOccupancyType.isPending || updateOccupancyType.isPending;

  const createForm = useForm<CreateOccupancyTypeRequest>({
    resolver: zodResolver(CreateOccupancyTypeRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateOccupancyTypeRequest>({
    resolver: zodResolver(UpdateOccupancyTypeRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (occupancyType) {
      updateForm.reset({
        code: occupancyType.code,
        name: occupancyType.name,
        paxCount: occupancyType.paxCount,
        sortOrder: occupancyType.sortOrder,
        isActive: occupancyType.isActive,
      });
    }
  }, [open, isCreate, occupancyType, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createOccupancyType.mutateAsync(values);
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!occupancyType) return;
    await updateOccupancyType.mutateAsync({ id: occupancyType.id, payload: values });
    onOpenChange(false);
  });

  const fields = <OccupancyTypeFields pending={pending} isCreate={isCreate} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add occupancy type' : `Edit ${occupancyType?.name ?? 'occupancy type'}`}
      description="The code is the stable identifier (e.g. SGL, DBL). Pax count drives pricing."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
